import {
  loginIcon,
  eyeIcon,
  usersIcon,
  downloadIcon,
  checkIcon,
  ImageIcon,
  TrashIcon,
  saveAs,
} from "@excalidraw/excalidraw/components/icons";
import { MainMenu } from "@excalidraw/excalidraw/index";
import React, { useState, useEffect, useCallback } from "react";

import { isDevEnv } from "@excalidraw/common";

import type { Theme } from "@excalidraw/element/types";

import { useAtom, useAtomValue, useSetAtom } from "../app-jotai";
import {
  currentProfileAtom,
  saveStateAtom,
  localBackupEnabledAtom,
  localBackupFolderHandleAtom,
  whiteboardsAtom,
} from "../app-jotai";
import {
  changeProfilePassword,
  updateProfilePicture,
  updateProfileLocalBackup,
} from "../data/firestore";
import { hashPassword, verifyPassword } from "../utils/passwordHash";
import {
  uploadProfilePicture,
  deleteProfilePicture,
  selectLocalBackupFolder,
  saveBackupFolderHandle,
  removeBackupFolderHandle,
  downloadWhiteboardData,
  saveWhiteboardLocally,
} from "../data/storage";

import { LanguageList } from "../app-language/LanguageList";

import { ChangePasswordDialog } from "./ChangePasswordDialog/ChangePasswordDialog";
import { ImageCropDialog } from "./ImageCropDialog";

import { saveDebugState } from "./DebugCanvas";

export const AppMainMenu: React.FC<{
  onCollabDialogOpen: () => any;
  isCollaborating: boolean;
  isCollabEnabled: boolean;
  theme: Theme | "system";
  setTheme: (theme: Theme | "system") => void;
  refresh: () => void;
}> = React.memo((props) => {
  const currentProfile = useAtomValue(currentProfileAtom);
  const setCurrentProfile = useSetAtom(currentProfileAtom);
  const saveState = useAtomValue(saveStateAtom);
  const [localBackupEnabled, setLocalBackupEnabled] = useAtom(
    localBackupEnabledAtom,
  );
  const [localBackupFolderHandle, setLocalBackupFolderHandle] = useAtom(
    localBackupFolderHandleAtom,
  );
  const whiteboards = useAtomValue(whiteboardsAtom);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<File | null>(null);
  const [isBackingUpAll, setIsBackingUpAll] = useState(false);

  const handleToggleLocalBackup = useCallback(async () => {
    if (!currentProfile) {
      return;
    }

    if (!localBackupEnabled) {
      // Enabling - need to select folder first
      const handle = await selectLocalBackupFolder();
      if (handle) {
        setLocalBackupFolderHandle(handle);
        setLocalBackupEnabled(true);
        await saveBackupFolderHandle(currentProfile.id, handle);
        await updateProfileLocalBackup(currentProfile.id, true);
      }
    } else {
      // Disabling
      setLocalBackupEnabled(false);
      setLocalBackupFolderHandle(null);
      await removeBackupFolderHandle(currentProfile.id);
      await updateProfileLocalBackup(currentProfile.id, false);
    }
  }, [
    currentProfile,
    localBackupEnabled,
    setLocalBackupFolderHandle,
    setLocalBackupEnabled,
  ]);

  const handleChangeBackupFolder = useCallback(async () => {
    if (!currentProfile) {
      return;
    }
    const handle = await selectLocalBackupFolder();
    if (handle) {
      setLocalBackupFolderHandle(handle);
      await saveBackupFolderHandle(currentProfile.id, handle);
    }
  }, [currentProfile, setLocalBackupFolderHandle]);

  const handleBackupAll = useCallback(async () => {
    if (!localBackupFolderHandle || whiteboards.length === 0) {
      return;
    }

    setIsBackingUpAll(true);
    let successCount = 0;
    let failCount = 0;

    for (const wb of whiteboards) {
      try {
        const data = await downloadWhiteboardData(wb.id);
        if (data) {
          await saveWhiteboardLocally(
            localBackupFolderHandle,
            wb.name,
            data.elements,
            data.files,
          );
          successCount++;
        }
      } catch (error) {
        console.error(`[BackupAll] Failed to backup ${wb.name}:`, error);
        failCount++;
      }
    }

    setIsBackingUpAll(false);
    alert(`Backup complete: ${successCount} saved, ${failCount} failed`);
  }, [localBackupFolderHandle, whiteboards]);

  const handleRemoveProfilePicture = useCallback(async () => {
    if (!currentProfile?.pictureUrl) {
      return;
    }

    if (!confirm("Remove profile picture?")) {
      return;
    }

    try {
      await deleteProfilePicture(currentProfile.id);
      await updateProfilePicture(currentProfile.id, "");

      setCurrentProfile({
        ...currentProfile,
        pictureUrl: undefined,
      });

      alert("Profile picture removed!");
    } catch (error) {
      console.error("Failed to remove profile picture:", error);
      alert("Failed to remove profile picture");
    }
  }, [currentProfile, setCurrentProfile]);

  // Listen for command palette events
  useEffect(() => {
    const handleChangePassword = () => setShowChangePassword(true);
    const handleRemovePicture = () => handleRemoveProfilePicture();

    window.addEventListener("excalidraw:change-password", handleChangePassword);
    window.addEventListener("excalidraw:remove-picture", handleRemovePicture);
    window.addEventListener(
      "excalidraw:toggle-backup",
      handleToggleLocalBackup,
    );
    window.addEventListener(
      "excalidraw:change-backup-folder",
      handleChangeBackupFolder,
    );
    window.addEventListener("excalidraw:backup-all", handleBackupAll);

    return () => {
      window.removeEventListener(
        "excalidraw:change-password",
        handleChangePassword,
      );
      window.removeEventListener(
        "excalidraw:remove-picture",
        handleRemovePicture,
      );
      window.removeEventListener(
        "excalidraw:toggle-backup",
        handleToggleLocalBackup,
      );
      window.removeEventListener(
        "excalidraw:change-backup-folder",
        handleChangeBackupFolder,
      );
      window.removeEventListener("excalidraw:backup-all", handleBackupAll);
    };
  }, [
    handleToggleLocalBackup,
    handleChangeBackupFolder,
    handleBackupAll,
    handleRemoveProfilePicture,
  ]);

  const handleProfilePictureSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];

    if (!file || !currentProfile) {
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image must be smaller than 5MB");
      return;
    }

    // Show crop dialog
    setImageToCrop(file);
    // Reset input
    event.target.value = "";
  };

  const handleProfilePictureUpload = async (croppedBlob: Blob) => {
    if (!currentProfile) {
      return;
    }

    console.log("Uploading cropped image:", croppedBlob.size); // eslint-disable-line no-console

    try {
      setIsUploadingPicture(true);
      setImageToCrop(null);

      // Delete old picture if exists
      if (currentProfile.pictureUrl) {
        await deleteProfilePicture(currentProfile.id);
      }

      // Convert blob to file
      const file = new File([croppedBlob], "profile.png", {
        type: "image/png",
      });

      // Upload new picture
      const url = await uploadProfilePicture(currentProfile.id, file);
      await updateProfilePicture(currentProfile.id, url);

      // Update local state
      setCurrentProfile({
        ...currentProfile,
        pictureUrl: url,
      });

      alert("Profile picture updated!");
    } catch (error) {
      console.error("Failed to upload profile picture:", error);
      alert("Failed to upload profile picture");
    } finally {
      setIsUploadingPicture(false);
    }
  };

  const handleSwitchProfile = () => {
    const message =
      saveState === "unsaved"
        ? "You have unsaved changes. Switch profile anyway?"
        : "Switch profile?";
    if (confirm(message)) {
      // Don't clear profile authentication - let it persist for easy switching back
      // Note: We also don't clear app-level passcode auth as it applies to all profiles
      setCurrentProfile(null);
      // Reload to show profile selector
      window.location.reload();
    }
  };

  const handleChangePassword = async (
    currentPassword: string,
    newPassword: string,
  ) => {
    if (!currentProfile) {
      throw new Error("No profile selected");
    }

    // Verify current password
    const isValid = await verifyPassword(
      currentPassword,
      currentProfile.password,
    );
    if (!isValid) {
      throw new Error("Current password is incorrect");
    }

    // Hash new password and update in Firestore
    const newPasswordHash = await hashPassword(newPassword);
    await changeProfilePassword(currentProfile.id, newPasswordHash);

    // Clear remembered authentication since password changed
    localStorage.removeItem(`profile_auth_${currentProfile.id}`);
    localStorage.removeItem(`profile_auth_${currentProfile.id}_expires`);

    // Update local profile state
    setCurrentProfile({
      ...currentProfile,
      password: newPasswordHash,
    });

    alert("Password changed successfully!");
  };

  return (
    <>
      <MainMenu
        triggerIcon={
          currentProfile?.pictureUrl ? (
            <img
              src={`${currentProfile.pictureUrl}?t=${Date.now()}`}
              alt={currentProfile.name}
              style={{
                width: "2rem",
                height: "2rem",
                borderRadius: "4px",
                objectFit: "cover",
              }}
            />
          ) : undefined
        }
      >
        {currentProfile && (
          <>
            <MainMenu.Group title={currentProfile.name}>
              <MainMenu.Item icon={usersIcon} onSelect={handleSwitchProfile}>
                Switch Profile
              </MainMenu.Item>
            </MainMenu.Group>
            <MainMenu.Group title="Settings">
              <MainMenu.Item
                icon={loginIcon}
                onSelect={() => setShowChangePassword(true)}
              >
                Change Password
              </MainMenu.Item>
              <MainMenu.Item
                icon={ImageIcon}
                onSelect={() =>
                  document.getElementById("profile-picture-input")?.click()
                }
              >
                {isUploadingPicture
                  ? "Uploading..."
                  : currentProfile.pictureUrl
                  ? "Change Profile Picture"
                  : "Upload Profile Picture"}
              </MainMenu.Item>
              {currentProfile.pictureUrl && (
                <MainMenu.Item
                  icon={TrashIcon}
                  onSelect={handleRemoveProfilePicture}
                >
                  Remove Profile Picture
                </MainMenu.Item>
              )}
              <MainMenu.Item
                icon={checkIcon}
                onSelect={handleToggleLocalBackup}
              >
                {localBackupEnabled
                  ? "Local Backup Enabled"
                  : "Enable Local Backup"}
              </MainMenu.Item>
              {localBackupEnabled && (
                <>
                  <MainMenu.Item
                    icon={saveAs}
                    onSelect={handleChangeBackupFolder}
                  >
                    Change Backup Folder
                  </MainMenu.Item>
                  <MainMenu.Item icon={downloadIcon} onSelect={handleBackupAll}>
                    {isBackingUpAll
                      ? `Backing up ${whiteboards.length}...`
                      : `Backup All (${whiteboards.length})`}
                  </MainMenu.Item>
                </>
              )}
            </MainMenu.Group>
            <MainMenu.Separator />
          </>
        )}
        <MainMenu.DefaultItems.LoadScene />
        <MainMenu.DefaultItems.SaveToActiveFile />
        <MainMenu.DefaultItems.Export />
        <MainMenu.DefaultItems.SaveAsImage />
        {props.isCollabEnabled && (
          <MainMenu.DefaultItems.LiveCollaborationTrigger
            isCollaborating={props.isCollaborating}
            onSelect={() => props.onCollabDialogOpen()}
          />
        )}
        <MainMenu.DefaultItems.CommandPalette className="highlighted" />
        <MainMenu.DefaultItems.SearchMenu />
        <MainMenu.DefaultItems.Help />
        <MainMenu.DefaultItems.ClearCanvas />
        <MainMenu.Separator />

        {isDevEnv() && (
          <MainMenu.Item
            icon={eyeIcon}
            onClick={() => {
              if (window.visualDebug) {
                delete window.visualDebug;
                saveDebugState({ enabled: false });
              } else {
                window.visualDebug = { data: [] };
                saveDebugState({ enabled: true });
              }
              props?.refresh();
            }}
          >
            Visual Debug
          </MainMenu.Item>
        )}
        <MainMenu.Separator />
        <MainMenu.DefaultItems.ToggleTheme
          allowSystemTheme
          theme={props.theme}
          onSelect={props.setTheme}
        />
        <MainMenu.ItemCustom>
          <LanguageList style={{ width: "100%" }} />
        </MainMenu.ItemCustom>
        <MainMenu.DefaultItems.ChangeCanvasBackground />
      </MainMenu>

      {/* Hidden file input for profile picture upload */}
      <input
        id="profile-picture-input"
        type="file"
        accept="image/*"
        onChange={handleProfilePictureSelect}
        style={{ display: "none" }}
      />

      {imageToCrop && (
        <ImageCropDialog
          image={imageToCrop}
          onCrop={handleProfilePictureUpload}
          onCancel={() => setImageToCrop(null)}
        />
      )}

      {showChangePassword && currentProfile && (
        <ChangePasswordDialog
          profile={currentProfile}
          onChangePassword={handleChangePassword}
          onCancel={() => setShowChangePassword(false)}
        />
      )}
    </>
  );
});
